'use client';

/**
 * Export Panel - Export entities, assessments, and relationships in various formats
 */

import { useState, useEffect } from 'react';
import type { Entity, Assessment } from '@/lib/command-center/types';

interface EntityLink {
  id: string;
  source_entity_id: string;
  target_entity_id: string;
  relationship_type: string;
  description?: string;
  created_at: string;
}

interface ExportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entities: Entity[];
  assessments: Assessment[];
  workspaceId: string | null;
  workspaceName: string;
}

type ExportFormat = 'csv' | 'json';
type ExportType = 'entities' | 'assessments' | 'relationships' | 'full-report';

export default function ExportPanel({
  isOpen,
  onClose,
  entities,
  assessments,
  workspaceId,
  workspaceName,
}: ExportPanelProps) {
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [exportType, setExportType] = useState<ExportType>('entities');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [relationships, setRelationships] = useState<EntityLink[]>([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);

  // Fetch relationships when panel opens or export type changes to relationships
  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    if (exportType === 'relationships' || exportType === 'full-report') {
      fetchRelationships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, exportType, workspaceId]);

  const fetchRelationships = async () => {
    if (!workspaceId || loadingRelationships) return;
    setLoadingRelationships(true);
    try {
      // Fetch links for all entities
      const allLinks: EntityLink[] = [];
      for (const entity of entities) {
        const res = await fetch(`/api/secure/command-center/entities/${entity.id}/links?workspace_id=${workspaceId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.links) {
            allLinks.push(...data.links);
          }
        }
      }
      // Deduplicate by id
      const uniqueLinks = Array.from(new Map(allLinks.map(l => [l.id, l])).values());
      setRelationships(uniqueLinks);
    } catch (err) {
      console.error('Failed to fetch relationships:', err);
    } finally {
      setLoadingRelationships(false);
    }
  };

  if (!isOpen) return null;

  const generateCSV = (data: Record<string, unknown>[], headers: string[]): string => {
    const escapeCSV = (val: unknown): string => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headerRow = headers.join(',');
    const dataRows = data.map(row =>
      headers.map(h => escapeCSV(row[h])).join(',')
    );

    return [headerRow, ...dataRows].join('\n');
  };

  const exportEntities = () => {
    if (exportFormat === 'json') {
      const data = entities.map(e => ({
        id: e.id,
        name: e.name,
        type: e.entity_type,
        status: e.status,
        description: e.description || '',
        tags: e.tags || [],
        metadata: includeMetadata ? e.metadata : undefined,
        created_at: e.created_at,
        updated_at: e.updated_at,
      }));
      return JSON.stringify(data, null, 2);
    } else {
      const headers = includeMetadata
        ? ['id', 'name', 'type', 'status', 'description', 'tags', 'metadata', 'created_at', 'updated_at']
        : ['id', 'name', 'type', 'status', 'description', 'tags', 'created_at', 'updated_at'];

      const data = entities.map(e => ({
        id: e.id,
        name: e.name,
        type: e.entity_type,
        status: e.status,
        description: e.description || '',
        tags: (e.tags || []).join('; '),
        metadata: includeMetadata ? JSON.stringify(e.metadata || {}) : undefined,
        created_at: e.created_at,
        updated_at: e.updated_at,
      }));
      return generateCSV(data, headers);
    }
  };

  const exportAssessments = () => {
    if (exportFormat === 'json') {
      const data = assessments.map(a => ({
        id: a.id,
        name: a.name,
        tool_id: a.tool_id,
        tool_category: a.tool_category,
        status: a.status,
        score_overall: a.score_overall,
        scores: a.scores,
        gaps_count: a.gaps?.length || 0,
        recommendations_count: a.recommendations?.length || 0,
        completed_at: a.completed_at,
        created_at: a.created_at,
      }));
      return JSON.stringify(data, null, 2);
    } else {
      const headers = ['id', 'name', 'tool_id', 'tool_category', 'status', 'score_overall', 'gaps_count', 'recommendations_count', 'completed_at', 'created_at'];
      const data = assessments.map(a => ({
        id: a.id,
        name: a.name,
        tool_id: a.tool_id,
        tool_category: a.tool_category,
        status: a.status,
        score_overall: a.score_overall ?? '',
        gaps_count: a.gaps?.length || 0,
        recommendations_count: a.recommendations?.length || 0,
        completed_at: a.completed_at || '',
        created_at: a.created_at,
      }));
      return generateCSV(data, headers);
    }
  };

  const exportRelationships = () => {
    if (exportFormat === 'json') {
      const data = relationships.map(r => {
        const source = entities.find(e => e.id === r.source_entity_id);
        const target = entities.find(e => e.id === r.target_entity_id);
        return {
          id: r.id,
          source_name: source?.name || 'Unknown',
          source_id: r.source_entity_id,
          target_name: target?.name || 'Unknown',
          target_id: r.target_entity_id,
          relationship_type: r.relationship_type,
          description: r.description || '',
          created_at: r.created_at,
        };
      });
      return JSON.stringify(data, null, 2);
    } else {
      const headers = ['id', 'source_name', 'source_id', 'target_name', 'target_id', 'relationship_type', 'description', 'created_at'];
      const data = relationships.map(r => {
        const source = entities.find(e => e.id === r.source_entity_id);
        const target = entities.find(e => e.id === r.target_entity_id);
        return {
          id: r.id,
          source_name: source?.name || 'Unknown',
          source_id: r.source_entity_id,
          target_name: target?.name || 'Unknown',
          target_id: r.target_entity_id,
          relationship_type: r.relationship_type,
          description: r.description || '',
          created_at: r.created_at,
        };
      });
      return generateCSV(data, headers);
    }
  };

  const exportFullReport = () => {
    const report = {
      workspace: workspaceName,
      exported_at: new Date().toISOString(),
      summary: {
        total_entities: entities.length,
        active_entities: entities.filter(e => e.status === 'active').length,
        archived_entities: entities.filter(e => e.status === 'archived').length,
        deprecated_entities: entities.filter(e => e.status === 'deprecated').length,
        total_assessments: assessments.length,
        draft_assessments: assessments.filter(a => a.status === 'draft').length,
        in_progress_assessments: assessments.filter(a => a.status === 'in_progress').length,
        completed_assessments: assessments.filter(a => a.status === 'completed').length,
        total_relationships: relationships.length,
        entity_types: Object.entries(
          entities.reduce((acc, e) => {
            acc[e.entity_type] = (acc[e.entity_type] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        ).map(([type, count]) => ({ type, count })),
      },
      entities: entities.map(e => ({
        id: e.id,
        name: e.name,
        type: e.entity_type,
        status: e.status,
        description: e.description,
        tags: e.tags,
        metadata: includeMetadata ? e.metadata : undefined,
      })),
      assessments: assessments.map(a => ({
        id: a.id,
        name: a.name,
        tool_id: a.tool_id,
        tool_category: a.tool_category,
        status: a.status,
        score_overall: a.score_overall,
        gaps_count: a.gaps?.length || 0,
      })),
      relationships: relationships.map(r => {
        const source = entities.find(e => e.id === r.source_entity_id);
        const target = entities.find(e => e.id === r.target_entity_id);
        return {
          source: source?.name,
          target: target?.name,
          type: r.relationship_type,
        };
      }),
    };

    return JSON.stringify(report, null, 2);
  };

  const handleExport = () => {
    setIsExporting(true);

    try {
      let content: string;
      let filename: string;
      let mimeType: string;

      const timestamp = new Date().toISOString().split('T')[0];

      switch (exportType) {
        case 'entities':
          content = exportEntities();
          filename = `${workspaceName.toLowerCase().replace(/\s+/g, '-')}-entities-${timestamp}.${exportFormat}`;
          break;
        case 'assessments':
          content = exportAssessments();
          filename = `${workspaceName.toLowerCase().replace(/\s+/g, '-')}-assessments-${timestamp}.${exportFormat}`;
          break;
        case 'relationships':
          content = exportRelationships();
          filename = `${workspaceName.toLowerCase().replace(/\s+/g, '-')}-relationships-${timestamp}.${exportFormat}`;
          break;
        case 'full-report':
          content = exportFullReport();
          filename = `${workspaceName.toLowerCase().replace(/\s+/g, '-')}-full-report-${timestamp}.json`;
          break;
        default:
          content = '';
          filename = 'export.txt';
      }

      mimeType = exportFormat === 'json' || exportType === 'full-report'
        ? 'application/json'
        : 'text/csv';

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-muted">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-muted rounded-lg">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Export Data</h2>
              <p className="text-sm text-foreground-tertiary">Download your workspace data</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Export Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">What to export</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'entities', label: 'Entities', count: entities.length, icon: '📦' },
                { value: 'assessments', label: 'Assessments', count: assessments.length, icon: '📋' },
                { value: 'relationships', label: 'Relationships', count: loadingRelationships ? '...' : relationships.length, icon: '🔗' },
                { value: 'full-report', label: 'Full Report', count: null, icon: '📊' },
              ].map(({ value, label, count, icon }) => (
                <button
                  key={value}
                  onClick={() => setExportType(value as ExportType)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-colors text-left ${
                    exportType === value
                      ? 'border-accent bg-accent-muted text-foreground'
                      : 'border-border text-foreground-secondary hover:border-border/40'
                  }`}
                >
                  <span className="text-lg">{icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{label}</div>
                    {count !== null && (
                      <div className="text-xs text-foreground-tertiary">{count} items</div>
                    )}
                  </div>
                  {exportType === value && (
                    <svg className="w-4 h-4 text-accent shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Format Selection (not for full report) */}
          {exportType !== 'full-report' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">Format</label>
              <div className="flex gap-2">
                {[
                  { value: 'csv', label: 'CSV', desc: 'Spreadsheet compatible' },
                  { value: 'json', label: 'JSON', desc: 'For developers' },
                ].map(({ value, label, desc }) => (
                  <button
                    key={value}
                    onClick={() => setExportFormat(value as ExportFormat)}
                    className={`flex-1 p-3 rounded-lg border transition-colors text-left ${
                      exportFormat === value
                        ? 'border-accent bg-accent-muted'
                        : 'border-border hover:border-border/40'
                    }`}
                  >
                    <div className="font-medium text-foreground">{label}</div>
                    <div className="text-xs text-foreground-tertiary">{desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Options */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                  includeMetadata ? 'bg-accent border-accent' : 'border-border/30'
                }`}
                onClick={() => setIncludeMetadata(!includeMetadata)}
              >
                {includeMetadata && (
                  <svg className="w-3 h-3 text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div>
                <span className="text-foreground text-sm">Include metadata fields</span>
                <p className="text-foreground-tertiary text-xs">Custom fields, owner, lifecycle dates</p>
              </div>
            </label>
          </div>

          {/* Preview Info */}
          <div className="bg-surface/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-foreground-muted shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-foreground-tertiary">
                {exportType === 'full-report' ? (
                  <span>Full report includes summary statistics, all entities, assessments, and relationships in JSON format.</span>
                ) : (
                  <span>
                    Export {exportType === 'entities' ? entities.length : exportType === 'assessments' ? assessments.length : relationships.length} {exportType}
                    {' '}in {exportFormat.toUpperCase()} format.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-muted bg-surface/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || (exportType === 'relationships' && loadingRelationships)}
            className="px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy"></div>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
